'use client';

import React, { useState, useEffect } from 'react';
import { MdClose, MdInstallMobile, MdPhoneAndroid } from 'react-icons/md';
import { useSelector } from 'react-redux';
import config from '@/config/config';

/**
 * PWA Install Banner - Shows on website pages when:
 * 1. PWA is enabled in settings
 * 2. User hasn't already installed the app
 * 3. User hasn't dismissed the banner recently
 * 4. showInstallBanner is enabled
 */
export default function PwaInstallBanner() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installData, setInstallData] = useState(null);
  const currentLanguage = useSelector((state) => state.language?.language || 'en');

  useEffect(() => {
    // Check if already dismissed
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) return;
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) return;

    // Fetch install data
    fetchInstallData();

    // Listen for install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    window.addEventListener('appinstalled', () => {
      setShow(false);
      setDeferredPrompt(null);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [currentLanguage]);

  const fetchInstallData = async () => {
    try {
      const res = await fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/pwa/install-data?language=${currentLanguage}`);
      const data = await res.json();
      if (data.success && data.data) {
        setInstallData(data.data);
        // Only show if banner is enabled
        setShow(true);
      }
    } catch {
      // Silently fail
    }
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShow(false);
      }
      setDeferredPrompt(null);
    } else {
      // Redirect to install page for manually guided installation
      window.location.href = '/app-install';
    }
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
  };

  if (!show || !installData) return null;

  const themeColor = installData.theme?.primaryColor || installData.themeColor || '#4F46E5';

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-3 sm:p-4 animate-slide-up">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        <div className="flex items-start gap-3 p-4">
          {/* Icon */}
          {installData.icon192 ? (
            <img
              src={installData.icon192}
              alt="App"
              className="w-12 h-12 rounded-xl shrink-0 shadow-sm"
            />
          ) : (
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${themeColor}20` }}
            >
              <MdPhoneAndroid className="w-6 h-6" style={{ color: themeColor }} />
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-800 text-sm">
              {installData.installBannerTitle || 'Install Our App'}
            </p>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
              {installData.installBannerDescription || 'Get the best experience'}
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="shrink-0 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <MdClose className="w-5 h-5" />
          </button>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 px-4 pb-4">

          <button
            onClick={handleInstall}
            className="px-4 py-2.5 rounded-xl text-gray-500 text-sm font-medium hover:bg-gray-100 transition-colors"
          >
            install
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
